import React from 'react';
import { useTranslation } from 'react-i18next';
import { 
    AlertTriangle, 
    AlertCircle,
    Sparkles,
    RefreshCw
} from 'lucide-react';

import { motion, AnimatePresence } from 'framer-motion';
import { useDeepVerdict } from '../hooks/useDeepVerdict';
import { usePagePayload } from '../hooks/usePagePayload';


interface VerdictDetail {
    caseKey: string;
    status: 'healthy' | 'neutral' | 'concerning' | 'critical' | 'growth' | 'idle';
    icon?: string;
    title: string;
    message: string;
    recommendation: string;
    details: string[];
    blockers?: Array<{
        key: string;
        label: string;
        icon: string;
    }>;
    metrics?: Array<{
        label: string;
        value: string | number;
        trend: 'up' | 'down' | 'stable';
        status: 'healthy' | 'neutral' | 'concerning' | 'critical';
    }>;
    params?: Record<string, any>;
}

interface AIVerdictModuleProps {
    verdict?: {
        heuristic: VerdictDetail | null;
        llm: string | null;
        lastAnalyzed: string | null;
    };
    pageId: string;
    boardId: string;
}



export const AIVerdictModule: React.FC<AIVerdictModuleProps> = ({ pageId, boardId }) => {
    const { t } = useTranslation();
    
    // Fetch fresh page payload including the heuristic verdict
    const { data: pagePayload, isLoading: isLoadingPayload } = usePagePayload(boardId, pageId);
    

    
    // Deep (LLM) analysis mutation
    const { mutate: analyze, isPending: isAnalyzing } = useDeepVerdict(boardId, pageId);

    const [localError, setLocalError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (localError) {
            const timer = setTimeout(() => setLocalError(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [localError]);

    const handleStartAnalysis = () => {
        setLocalError(null);
        analyze(undefined, {
            onError: (err: any) => {
                // Commercial error mapping
                let messageValue = t('verdicts.errors.default', 'Our AI strategist is currently unavailable. Please try again in a moment.');
                
                if (err?.status === 403 || err?.code === 'AI_QUOTA_EXCEEDED') {
                    messageValue = t('verdicts.errors.quota', 'Your strategic analysis quota for this week has been reached.');
                } else if (err?.status === 404) {
                    messageValue = t('verdicts.errors.notFound', 'We couldn\'t find the data needed for a deep analysis.');
                } else if (err?.status === 429) {
                    messageValue = t('verdicts.errors.rateLimit', 'Too many requests. Please wait a few seconds.');
                }

                setLocalError(messageValue);
            }
        });
    };

    // --- THREE STATE LOGIC ---
    // Backend states: 'empty' (no data), 'heuristic' (fresh rule-based), 'static' (legacy cached)
    // Frontend-only state: 'llm' (set via useDeepVerdict cache update, session-only)
    const [chatInput, setChatInput] = React.useState('');
    const aiState = pagePayload?.aiVerdictState || 'empty';
    const isLLMMode = aiState === 'llm';

    const type = pagePayload?.type || 'performance';
    const verdict = pagePayload?.aiVerdict;
    const heuristic = verdict?.heuristic;
    const llmAnalysis = verdict?.llm;
    const caseKey = heuristic?.caseKey || 'EMPTY_STATE';

    // Translation helper
    const getTranslation = (field: 'title' | 'message' | 'recommendation') => {
        const key = `verdicts.${type}.${caseKey}.${field}`;
        const params = (heuristic as any)?.params || {};
        return t(key, { 
            defaultValue: (heuristic as any)?.[field], 
            ...params 
        });
    };

    // Ensure heuristic has at least basic values if missing or in EMPTY_STATE
    const effectiveHeuristic = (heuristic && caseKey !== 'EMPTY_STATE') ? heuristic : {
        ...heuristic,
        caseKey: 'EMPTY_STATE',
        status: 'idle',
        title: t(`verdicts.${type}.EMPTY_STATE.title`, t('verdicts.empty.title', 'Gathering Signals')),
        message: t(`verdicts.${type}.EMPTY_STATE.message`, t('verdicts.empty.message', 'Connect your channels or add historical data to allow the AI to generate a verdict.')),
        recommendation: t(`verdicts.${type}.EMPTY_STATE.recommendation`, t('verdicts.performance.EMPTY_STATE.recommendation', 'Connect more data sources to unlock deep performance insights.')),
        details: heuristic?.details || [],
        metrics: heuristic?.metrics || [],
        blockers: heuristic?.blockers || []
    } as VerdictDetail;

    const finalTitle = verdict?.title || getTranslation('title') as string || effectiveHeuristic.title;
    const finalMessage = verdict?.message || getTranslation('message') as string || effectiveHeuristic.message;
    const finalRecommendation = verdict?.recommendation || getTranslation('recommendation') as string || effectiveHeuristic.recommendation;
    const finalDetails = verdict?.details || effectiveHeuristic.details || [];
    const finalMetrics = verdict?.metrics || effectiveHeuristic.metrics || [];

    if (isLoadingPayload) {
        return (
            <div className="h-[434px] flex flex-col bg-accent-primary/5 dark:bg-accent-primary/10 rounded-2xl border border-accent-primary/10 dark:border-accent-primary/20 relative overflow-hidden group backdrop-blur-sm transition-all duration-300 shadow-sm">
                <div className="absolute inset-0 z-10">
                    <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/20 dark:via-white/5 to-transparent shadow-[0_0_40px_rgba(255,255,255,0.1)]" />
                </div>
                <div className="p-6 flex-1 flex flex-col relative z-0">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="w-1.5 h-1.5 rounded-full bg-accent-primary/40" />
                        <div className="h-2 w-24 bg-accent-primary/20 rounded-full" />
                    </div>
                    <div className="h-6 w-3/4 bg-accent-primary/10 rounded-lg mb-6" />
                    <div className="space-y-4 flex-1">
                        <div className="h-3 w-full bg-accent-primary/5 rounded-full" />
                        <div className="h-3 w-full bg-accent-primary/5 rounded-full" />
                    </div>
                    <div className="mt-auto space-y-4">
                        <div className="h-24 w-full bg-white dark:bg-neutral-800/40 rounded-xl border border-neutral-100 dark:border-neutral-700/50" />
                        <div className="h-12 w-full bg-accent-primary/15 rounded-xl border border-accent-primary/20" />
                    </div>
                </div>
                <style>{`@keyframes shimmer { 100% { transform: translateX(100%); } }`}</style>
            </div>
        );
    }

    const handleAskAI = () => {
        if (!chatInput.trim()) return;
        
        // Trigger global AI banner with context
        const event = new CustomEvent('dashbal:trigger-ai', {
            detail: {
                prompt: chatInput,
                context: {
                    pageId,
                    boardId,
                    pageType: type,
                    verdict: verdict
                }
            }
        });
        window.dispatchEvent(event);
        setChatInput('');
    };

    return (
        <div className="h-full flex flex-col bg-accent-primary/5 dark:bg-accent-primary/10 rounded-2xl border border-accent-primary/10 dark:border-accent-primary/20 relative overflow-hidden group backdrop-blur-sm transition-all duration-300">
            <div className="absolute top-0 right-0 p-6 opacity-10 group-hover:scale-110 transition-transform duration-500 pointer-events-none">
                <span className="w-24 h-24 text-accent-primary" style={{ maskImage: `url(https://cdn.dashbal.com/assets/icon_51112e7d.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_51112e7d.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block' }} role='img' />
            </div>

            <div className="p-6 pt-5 flex-1 flex flex-col relative z-10">
                <div className="text-[10px] font-bold text-accent-primary uppercase tracking-widest mb-2 flex items-center gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full bg-accent-primary ${isAnalyzing ? 'animate-spin' : 'animate-pulse'}`} />
                    {isLLMMode ? t('verdicts.labels.aiChat', 'Deep AI Analysis') : t('verdicts.labels.aiInsight')}
                </div>

                <h4 className="font-bold text-lg text-text-primary mb-1.5">
                    {finalTitle}
                </h4>

                <div className="flex-1 overflow-auto custom-scrollbar pr-1">
                    <p className="text-neutral-600 dark:text-neutral-300 text-sm leading-relaxed mb-4 font-medium">
                        {finalMessage.split('\n').map((para: string, i: number) => (
                            <span key={i} className="block mb-2 last:mb-0">{para}</span>
                        ))}
                    </p>

                    {effectiveHeuristic.blockers && effectiveHeuristic.blockers.length > 0 && (
                         <div className="mb-5 space-y-2">
                            <div className="text-[10px] font-black uppercase tracking-widest text-rose-500/80 mb-2">
                                {t('verdicts.labels.missingSources', 'Missing for Full Analysis:')}
                            </div>
                            {effectiveHeuristic.blockers.map((blocker: { key: string; label: string; icon: string }) => (
                                <motion.div 
                                    key={blocker.key} 
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="flex items-center gap-3 p-2.5 bg-rose-50/50 dark:bg-rose-900/10 border border-rose-100/50 dark:border-rose-800/20 rounded-xl group/blocker"
                                >
                                    <div className="w-8 h-8 rounded-lg bg-white dark:bg-neutral-800 flex items-center justify-center shadow-sm text-rose-500">
                                        <AlertTriangle className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1 text-[10px] font-bold text-text-primary uppercase tracking-tight">
                                        {blocker.label}
                                    </div>
                                </motion.div>
                            ))}
                         </div>
                    )}

                    {finalMetrics && finalMetrics.length > 0 && (
                        <div className="mb-5 grid grid-cols-2 gap-3">
                            {finalMetrics.map((m: any, i: number) => (
                                <div key={i} className="p-3 bg-neutral-50 dark:bg-neutral-800/50 rounded-xl border border-neutral-100 dark:border-neutral-700/50">
                                    <div className="text-[9px] font-bold text-neutral-400 uppercase tracking-widest mb-1">{m.label}</div>
                                    <div className="flex items-baseline gap-2">
                                        <div className="text-sm font-black text-text-primary">{m.value}</div>
                                        {m.trend && (
                                            <div className={`text-[9px] font-bold ${m.status === 'healthy' ? 'text-green-500' : m.status === 'critical' ? 'text-rose-500' : 'text-amber-500'}`}>
                                                {m.trend === 'up' ? '↑' : m.trend === 'down' ? '↓' : '→'}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {finalDetails && finalDetails.length > 0 && (
                        <div className="mb-5 space-y-2">
                            <div className="text-[10px] font-black uppercase tracking-widest text-accent-primary mb-1">
                                {t('verdicts.labels.strategicInsights', 'Strategic Assessment:')}
                            </div>
                            {finalDetails.map((detail: string, i: number) => (
                                <div key={i} className="flex gap-3 text-xs font-semibold text-neutral-600 dark:text-neutral-400 leading-tight">
                                    <div className="w-1.5 h-1.5 rounded-full bg-accent-primary/40 mt-1 flex-shrink-0" />
                                    {detail}
                                </div>
                            ))}
                        </div>
                    )}

                    {llmAnalysis && !isLLMMode && (
                        <motion.div 
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="mb-5 pt-5 border-t border-accent-primary/10"
                        >
                            <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-accent-primary mb-2">
                                <Sparkles className="w-3.5 h-3.5 animate-pulse" />
                                {t('verdicts.llm.analysisTitle')}
                            </div>
                            <div className="text-sm text-neutral-600 dark:text-neutral-300 leading-relaxed font-medium bg-accent-primary/5 p-4 rounded-xl border border-accent-primary/10 italic">
                                {llmAnalysis.split('\n').map((para: string, i: number) => (
                                    <p key={i} className="mb-3 last:mb-0">"{para}"</p>
                                ))}
                            </div>
                        </motion.div>
                    )}

                    <AnimatePresence>
                        {localError && (
                            <motion.div 
                                initial={{ opacity: 0, y: -10, scale: 0.95 }} 
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.95, transition: { duration: 0.2 } }}
                                className="p-3 bg-rose-50 dark:bg-rose-900/10 border border-rose-100 dark:border-rose-900/20 rounded-xl mb-4 text-[10px] font-bold text-rose-600 dark:text-rose-400 flex items-center gap-2"
                            >
                                <AlertCircle className="w-4 h-4" />
                                {localError}
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <div className="mt-auto pt-6 space-y-4">
                    {!isLLMMode ? (
                        <div className="bg-white dark:bg-neutral-800/50 rounded-xl p-4 border border-neutral-100 dark:border-neutral-700 shadow-sm transition-all hover:shadow-md">
                            <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-400 dark:text-neutral-500 mb-2">
                                {t('verdicts.labels.recommendation') as string}
                            </div>
                            <div className="text-xs font-semibold text-text-primary leading-relaxed">
                                {finalRecommendation}
                            </div>
                        </div>
                    ) : null}

                    <AnimatePresence mode="wait">
                        {!isLLMMode ? (
                            <motion.button 
                                key="analyze"
                                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                onClick={handleStartAnalysis}
                                disabled={isAnalyzing}
                                className="w-full py-3.5 bg-accent-primary text-white text-[11px] font-black uppercase tracking-widest rounded-xl shadow-lg shadow-accent-primary/20 hover:shadow-accent-primary/40 hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-2.5 disabled:opacity-50"
                            >
                                {isAnalyzing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                                {isAnalyzing ? t('verdicts.actions.analyzing') : t('verdicts.actions.startAnalysis')}
                            </motion.button>
                        ) : (
                            <motion.div 
                                key="ask-ai"
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="relative group/input"
                            >
                                <input 
                                    type="text"
                                    value={chatInput}
                                    onChange={(e) => setChatInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleAskAI()}
                                    placeholder={t('verdicts.actions.askAiMore', 'Ask AI more about this...') as string}
                                    className="w-full pl-4 pr-12 py-3.5 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-700 rounded-xl text-xs font-medium focus:ring-2 focus:ring-accent-primary/20 focus:border-accent-primary outline-none transition-all shadow-sm"
                                />
                                <button 
                                    onClick={handleAskAI}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center bg-accent-primary text-white rounded-lg hover:bg-accent-primary/90 transition-colors shadow-sm"
                                >
                                    <Sparkles className="w-4 h-4" />
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
};
